import {Flex, Grid} from 'exsportia-components'
import {FC, useCallback, useMemo} from 'react'
import {useQuery} from 'react-query'
import {useLocation, useNavigate, useParams} from 'react-router-dom'

import {MemberQueries} from '../../api/member'
import {UserExsportiaCardModal} from '../../components/user-exsportia-card-modal/user-exsportia-card-modal'
import {DownloadAppModal} from '../../modals/download-app-modal/download-app-modal'
import {useAuthState} from '../../services/auth/hooks'
import {useAppLoading} from '../../services/global/hooks'
import {UserService} from '../../services/user'
import {DropdownMenu} from '../../ui/components/dropdown-menu/dropdown-menu'
import {TabButton, TabButtonProps} from '../../ui/components/tab-button/tab-button'
import {auth} from '../../utils/firebase'
import {LocaleOptions, useLocale} from '../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {ContactQRModal} from './contact-qr-modal'
import {profileMenuOptions} from './profile-menu'

const defaultLOptions: LocaleOptions = {
  caseAction: 'capitalize',
}

const tabs = ['profile', 'bookings', 'subscription-bookings']

export const MobileNavMenu: FC = () => {
  const {l, language} = useLocale()
  const {pathname} = useLocation()
  const {openModal} = useModalActions()
  const {isLoading} = useAppLoading()
  const {signOut, isLoggedIn} = useAuthState()
  const navigate = useNavigate()
  const {venueId} = useParams()
  const {data: user} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const {data: member} = useQuery(
    MemberQueries.getMemberByVenueQuery({query: {venueId, userId: auth.currentUser?.uid}}),
  )
  const name = useMemo(() => {
    if (!user) {
      return null
    }
    const nameToReturn = []
    if (user.firstName) {
      nameToReturn.push(user.firstName)
      if (user.lastName) {
        nameToReturn.push(` ${user.lastName[0]}.`)
      }
    } else {
      nameToReturn.push(user.name)
    }
    return nameToReturn.join('')
  }, [user])
  const isContact = useMemo(() => isNotNilAndNotEmpty(member?.uid), [member?.uid])
  const handleOpenQR = useCallback(() => {
    openModal({
      component: <ContactQRModal venueId={venueId} />,
      footerConfig: {
        disabled: true,
      },
    })
  }, [openModal])
  const openDownloadAppModal = useCallback(
    () =>
      openModal({
        title: l('profile'),
        component: (
          <>
            <>
              <DownloadAppModal />
              <UserExsportiaCardModal
                name={name || ''}
                avatar={user?.avatar}
                subText={member?.uid || user?.email || ''}
                handleOpenQr={member?.uid ? handleOpenQR : null}
                withDescription={false}
                phone={user?.phone}
                phoneClaimed={user?.phone_claimed}
              />
            </>
          </>
        ),
        footerConfig: {
          disabled: true,
        },
      }),
    [name, user, member],
  )
  const menuSettings: TabButtonProps[] = useMemo(() => {
    const pathArr = pathname.split('/').filter((el) => el !== '')
    const activeTab = tabs.includes(pathArr[1]) ? pathArr[1] : 'home'
    return [
      {
        text: l('home', defaultLOptions),
        icon: 'houseAction',
        isActive: activeTab === 'home',
        onClick: () => navigate(`/${venueId}`),
      },
      {
        text: l('bookings', defaultLOptions),
        isActive: activeTab === 'bookings',
        icon: 'bookings',
        onClick: () => navigate(`/${venueId}/bookings`),
      },
      {
        text: l('subscriptions', defaultLOptions),
        icon: 'crownAction',
        isActive: activeTab === 'subscription-bookings',
        disabled: !isLoggedIn,
        onClick: () => navigate(`/${venueId}/subscription-bookings`),
      },
    ]
  }, [l, pathname, venueId])
  const menuOptions = profileMenuOptions({
    l,
    signOut,
    language,
    isContact,
    isLoading,
    openQR: handleOpenQR,
    openOrders: () => navigate(`/${venueId}/orders`),
    openVouchers: () => navigate(`/${venueId}/vouchers`),
    openRemoveAccount: () => navigate(`/${venueId}/remove-account`),
    openProfile: () => openDownloadAppModal(),
  })
  return (
    <Flex
      justifyContent='space-between'
      width='100%'
    >
      <Grid
        gridTemplateColumns='repeat(4, 1fr)'
        width='100%'
      >
        {menuSettings.map((settings, index) => (
          <TabButton
            key={`mobile-tab-${index}`}
            isMobile={true}
            icon={settings.icon}
            text={settings.text}
            onClick={settings.onClick}
            isActive={settings.isActive}
          />
        ))}
        <Flex
          width='100%'
          justifyContent='center'
        >
          <DropdownMenu
            name='profile'
            options={menuOptions}
            height='100%'
          >
            <TabButton
              isMobile={true}
              icon='user'
              text={l('profile')}
            />
          </DropdownMenu>
        </Flex>
      </Grid>
    </Flex>
  )
}
